import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiRequestError } from "../api/client.js";
import { useApiClient } from "../api/ApiClientContext.js";
import { createEmployee, listManagedEmployees, updateEmployee } from "../api/employees.js";
import type { WireEmployeeWithCode } from "../api/types.js";
import { describeApiError } from "../domain/errors.js";

const MIN_CODE_LENGTH = 2;
const DUPLICATE_CODE_MESSAGE = "Ta PIN je že v uporabi. Izberite drugega.";

/** The duplicate-PIN 409 is the error an admin will actually hit, so it gets plain wording; anything else falls back to the generic mapper. */
function describeEmployeeError(err: unknown): string {
  const isDuplicate =
    err instanceof ApiRequestError && err.failure.kind === "conflict" && err.failure.reason === "duplicate-code";
  return isDuplicate ? DUPLICATE_CODE_MESSAGE : describeApiError(err);
}

/**
 * Admin management of employee codes (PINs) — the counter secret every intake,
 * pickup and deletion is attributed to. Rendered inside the "Uporabniki" screen
 * (`AccountsPage`), which is already admin-gated by the route's `RequireAdmin`.
 *
 * Unlike the desktop wizard's `EmployeeManager`, this both shows and edits the
 * code: the shop owner manages everyone's PIN here, backed by
 * `GET /api/employees/managed` — the one endpoint that returns codes.
 * Deactivation, not deletion, is the removal path: an inactive employee still
 * has to exist for the `audit_log` rows that reference them.
 */
export function EmployeeCodes() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const employeesQuery = useQuery({ queryKey: ["employees", "managed"], queryFn: () => listManagedEmployees(client) });

  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [code, setCode] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["employees"] });

  const createMutation = useMutation({
    mutationFn: () => createEmployee(client, { name: name.trim(), lastname: lastname.trim(), code: code.trim() }),
    onSuccess: async () => {
      setName("");
      setLastname("");
      setCode("");
      setAddError(null);
      await invalidate();
    },
    onError: (err) => setAddError(describeEmployeeError(err)),
  });

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate();
  }

  const employees = employeesQuery.data?.employees ?? [];

  return (
    <section className="employee-codes">
      <h2>Kode zaposlenih</h2>
      <p className="employee-codes-hint">
        PIN je potreben za vsak vnos, dvig in brisanje na blagajni. Vsak zaposleni (tudi vi) potrebuje svojega.
      </p>

      <form className="accounts-create-form" onSubmit={handleAdd}>
        <div className="form-field">
          <label htmlFor="new-employee-name">Ime</label>
          <input
            id="new-employee-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="off"
            disabled={createMutation.isPending}
          />
        </div>
        <div className="form-field">
          <label htmlFor="new-employee-lastname">Priimek</label>
          <input
            id="new-employee-lastname"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            required
            autoComplete="off"
            disabled={createMutation.isPending}
          />
        </div>
        <div className="form-field">
          <label htmlFor="new-employee-code">PIN</label>
          <input
            id="new-employee-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            inputMode="numeric"
            minLength={MIN_CODE_LENGTH}
            maxLength={8}
            autoComplete="off"
            disabled={createMutation.isPending}
          />
        </div>
        <button type="submit" disabled={createMutation.isPending}>
          Dodaj zaposlenega
        </button>
        {addError && (
          <p className="form-error" role="alert">
            {addError}
          </p>
        )}
      </form>

      {employeesQuery.isPending && (
        <p role="status" className="accounts-status">
          Nalaganje …
        </p>
      )}
      {employeesQuery.isError && (
        <p role="alert" className="accounts-status">
          Napaka pri nalaganju zaposlenih.
        </p>
      )}
      {employeesQuery.isSuccess && employees.length === 0 && (
        <p className="accounts-status">Nobenega zaposlenega še ni. Dodajte vsaj enega, sicer vnosa ni mogoče shraniti.</p>
      )}
      {employeesQuery.isSuccess && employees.length > 0 && (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Ime</th>
              <th>Priimek</th>
              <th>PIN</th>
              <th>Stanje</th>
              <th>Dejanja</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <EmployeeCodeRow key={employee.id} employee={employee} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function EmployeeCodeRow({ employee }: { employee: WireEmployeeWithCode }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [newCode, setNewCode] = useState(employee.code);
  const [rowError, setRowError] = useState<string | null>(null);

  const patchMutation = useMutation({
    mutationFn: (patch: { active?: boolean; code?: string }) => updateEmployee(client, employee.id, patch),
    onSuccess: async () => {
      setRowError(null);
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
    onError: (err) => setRowError(describeEmployeeError(err)),
  });

  function handleCodeSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = newCode.trim();
    if (trimmed.length < MIN_CODE_LENGTH) {
      setRowError(`PIN mora imeti vsaj ${MIN_CODE_LENGTH} znaka.`);
      return;
    }
    patchMutation.mutate({ code: trimmed }, { onSuccess: () => setEditing(false) });
  }

  return (
    <>
      <tr className={employee.active ? "" : "employee-inactive"}>
        <td>{employee.name}</td>
        <td>{employee.lastname}</td>
        <td className="employee-code-cell">{employee.code}</td>
        <td>{employee.active ? "Aktiven" : "Neaktiven"}</td>
        <td className="accounts-row-actions">
          <button
            type="button"
            onClick={() => {
              setNewCode(employee.code);
              setEditing((v) => !v);
            }}
            disabled={patchMutation.isPending}
          >
            Spremeni PIN
          </button>
          <button
            type="button"
            onClick={() => patchMutation.mutate({ active: !employee.active })}
            disabled={patchMutation.isPending}
          >
            {employee.active ? "Deaktiviraj" : "Aktiviraj"}
          </button>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={5}>
            <form className="accounts-reset-form" onSubmit={handleCodeSubmit}>
              <label htmlFor={`code-${employee.id}`}>
                Nov PIN za {employee.name} {employee.lastname}
              </label>
              <input
                id={`code-${employee.id}`}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                inputMode="numeric"
                minLength={MIN_CODE_LENGTH}
                maxLength={8}
                autoComplete="off"
                disabled={patchMutation.isPending}
              />
              <button type="submit" disabled={patchMutation.isPending}>
                Shrani
              </button>
              <button type="button" onClick={() => setEditing(false)} disabled={patchMutation.isPending}>
                Prekliči
              </button>
            </form>
          </td>
        </tr>
      )}
      {rowError && (
        <tr>
          <td colSpan={5}>
            <p className="form-error" role="alert">
              {rowError}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
